/**
 * useResponsive Hook - Centralized responsive utilities for the application
 * Provides breakpoint detection, responsive values, and layout helpers
 */

import { useWindowDimensions } from 'react-native';

import { tokens } from '@design';
// Breakpoint definitions (matching design system)
export const BREAKPOINTS = {
  xs: 0,      // Extra small (phones portrait)
  sm: 375,    // Small phones (iPhone SE, etc.)
  md: 640,    // Mobile breakpoint (< 640px = mobile)
  lg: 1024,   // Tablet breakpoint (640-1024 = tablet)
  xl: 1280,   // Desktop (> 1024px = desktop)
  xxl: 1920,  // Large desktops
} as const;

// Consistent spacing system
export const SPACING = {
  // Base spacing units
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,

  // Component-specific spacing
  containerPaddingMobile: 16,
  containerPaddingTablet: 24,
  containerPaddingDesktop: 32,

  cardPaddingMobile: 12,
  cardPaddingTablet: 16,
  cardPaddingDesktop: 20,

  sectionSpacing: 24,
  cardGap: 16,
  cardGapMobile: 12,
} as const;

export interface ResponsiveValues {
  // Device type detection
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isSmallMobile: boolean;
  isLargeDesktop: boolean;

  // Dimensions
  width: number;
  height: number;

  // Responsive padding/margins
  containerPadding: number;
  cardPadding: number;
  sectionSpacing: number;

  // Font sizes (responsive)
  fontSize: {
    h1: number;
    h2: number;
    h3: number;
    body: number;
    small: number;
    caption: number;
  };

  // Spacing scale (responsive)
  spacing: {
    xs: number;
    sm: number;
    md: number;
    lg: number;
    xl: number;
    xxl: number;
  };

  // Grid columns
  gridColumns: number;

  // Card width helpers
  cardWidth: {
    full: string;
    half: string | number;
    third: string | number;
    quarter: string | number;
  };

  // Modal dimensions
  modalWidth: number | string;
  modalMaxWidth: string;

  // Table behavior
  tableScrollable: boolean;

  // Touch target minimum size
  touchTargetSize: number;

  // Horizontal scroll card width (for KPI cards, initiative cards, etc.)
  horizontalCardWidth: number;

  // Helper functions
  responsive: <T>(mobile: T, tablet: T, desktop: T) => T;
  responsiveValue: <T>(options: { mobile?: T; tablet?: T; desktop?: T; default: T }) => T;
}

export const useResponsive = (): ResponsiveValues => {
  const { width, height } = useWindowDimensions();

  // Device type detection (Mobile < 640px, Tablet 640-1024px, Desktop > 1024px)
  const isSmallMobile = width < BREAKPOINTS.sm;  // < 375px
  const isMobile = width < BREAKPOINTS.md;        // < 640px
  const isTablet = width >= BREAKPOINTS.md && width < BREAKPOINTS.lg;  // 640-1024px
  const isDesktop = width >= BREAKPOINTS.lg;      // > 1024px
  const isLargeDesktop = width >= BREAKPOINTS.xxl; // > 1920px

  // Responsive helper function
  const responsive = <T,>(mobile: T, tablet: T, desktop: T): T => {
    if (isMobile) return mobile;
    if (isTablet) return tablet;
    return desktop;
  };

  // Advanced responsive value helper
  const responsiveValue = <T,>(options: { mobile?: T; tablet?: T; desktop?: T; default: T }): T => {
    if (isMobile && options.mobile !== undefined) return options.mobile;
    if (isTablet && options.tablet !== undefined) return options.tablet;
    if (isDesktop && options.desktop !== undefined) return options.desktop;
    return options.default;
  };

  return {
    // Device type detection
    isMobile,
    isTablet,
    isDesktop,
    isSmallMobile,
    isLargeDesktop,

    // Dimensions
    width,
    height,

    // Responsive padding/margins (with small mobile support)
    containerPadding: isSmallMobile ? 12 : responsive(16, 24, 32),
    cardPadding: isSmallMobile ? 10 : responsive(12, 16, 20),
    sectionSpacing: isSmallMobile ? 12 : responsive(16, 24, 32),

    // Font sizes
    fontSize: {
      h1: responsive(22, 28, 32),
      h2: responsive(18, 22, 24),
      h3: responsive(16, 18, 20),
      body: responsive(14, 15, 16),
      small: responsive(12, 13, 14),
      caption: responsive(10, 11, 12),
    },

    // Spacing scale
    spacing: {
      xs: responsive(4, 6, 8),
      sm: responsive(8, 10, 12),
      md: responsive(12, 14, 16),
      lg: responsive(16, 20, 24),
      xl: responsive(24, 28, 32),
      xxl: responsive(32, 40, 48),
    },

    // Grid columns for layouts
    gridColumns: responsive(1, 2, 3),

    // Card width helpers
    cardWidth: {
      full: '100%',
      half: isMobile ? '100%' : '48%',
      third: isMobile ? '100%' : isTablet ? '48%' : '31%',
      quarter: isMobile ? '100%' : isTablet ? '48%' : '23%',
    },

    // Modal dimensions
    modalWidth: responsive('95%', '80%', 600),
    modalMaxWidth: '95%',

    // Table behavior
    tableScrollable: isMobile,

    // Touch target minimum size (44px is Apple's recommendation)
    touchTargetSize: 44,

    // Horizontal scroll card width (responsive)
    // Mobile: full width minus padding (shows ~1.2 cards as hint)
    // Tablet: 280px (shows ~2-3 cards)
    // Desktop: 320px (shows ~3-4 cards)
    horizontalCardWidth: isMobile ? width - 48 : isTablet ? 280 : 320,

    // Helper functions
    responsive,
    responsiveValue,
  };
};

/**
 * Responsive style helpers for common patterns
 */
export const responsiveStyles = {
  // Flex direction that switches on mobile
  flexRowOnDesktop: (isMobile: boolean) => ({
    flexDirection: isMobile ? 'column' as const : 'row' as const,
  }),

  // Flex direction that switches on mobile (RTL aware - row-reverse for desktop)
  flexRowReverseOnDesktop: (isMobile: boolean) => ({
    flexDirection: isMobile ? 'column' as const : 'row-reverse' as const,
  }),

  // Full width on mobile, fixed width on desktop
  responsiveWidth: (isMobile: boolean, desktopWidth: number) => ({
    width: isMobile ? '100%' as const : desktopWidth,
  }),

  // Container padding
  containerPadding: (isMobile: boolean, isTablet: boolean) => ({
    paddingHorizontal: isMobile ? 16 : isTablet ? 24 : 32,
    paddingVertical: isMobile ? 16 : isTablet ? 20 : 24,
  }),

  // Card layout that stacks on mobile
  cardGrid: (isMobile: boolean, isTablet: boolean) => ({
    flexDirection: 'row' as const,
    flexWrap: 'wrap' as const,
    gap: isMobile ? 12 : 16,
    justifyContent: 'flex-start' as const,
  }),

  // Hide element on mobile
  hideOnMobile: (isMobile: boolean) => ({
    display: isMobile ? 'none' as const : 'flex' as const,
  }),

  // Show element only on mobile
  showOnlyOnMobile: (isMobile: boolean) => ({
    display: isMobile ? 'flex' as const : 'none' as const,
  }),

  // Horizontal card scroll container styles
  horizontalScrollContainer: () => ({
    flexGrow: 0,
  }),

  // Horizontal card row content container styles
  horizontalCardsRow: () => ({
    flexDirection: 'row' as const,
    gap: tokens.spacing.absolute["4"],
    paddingHorizontal: tokens.spacing.absolute["1"],
    paddingBottom: tokens.spacing.absolute["3"],
  }),

  // Horizontal card item styles (use with horizontalCardWidth from hook)
  horizontalCardItem: (cardWidth: number) => ({
    width: cardWidth,
    flexShrink: 0,
  }),
};

export default useResponsive;
